import {useEffect} from 'react'
import {useQuery} from '@tanstack/react-query'
import {useNavigate, useParams} from 'react-router-dom'
import {useServices} from '@/services/context'
import {usePageTitle} from '@/hooks/use-page-title'
import {useAuth} from '@/contexts/auth-context'
import {ApprovalPolicyForm} from '@/components/ApprovalPolicyForm'
import {useI18n} from '@/i18n'
import {routes} from '@/lib/routes'
import {permissions} from '@/lib/permissions'
import type {CreateApprovalPolicyRequest, UpdateApprovalPolicyRequest} from '@/types/approval-policy'
import {PageLayout} from '@/components/shared/PageLayout'
import {queryError} from '@/lib/query-utils'
import {useEntityMutation} from '@/hooks/useEntityMutation'
import {CONCEPT_ICON} from '@/lib/concepts'

export function ApprovalPolicyEdit() {
    const i18n = useI18n()
    const services = useServices()
    usePageTitle(i18n.approvalPolicies.editPageTitle)
    const navigate = useNavigate()
    const {id} = useParams<{id: string}>()
    const {hasPermission} = useAuth()

    const canUpdate = hasPermission(permissions.approvalPolicies.update)

    const {data: policy, isLoading, error: fetchError} = useQuery({
        queryKey: ['approval-policy', id],
        queryFn: () => services.approvalPolicies.detail(id!),
        enabled: !!id,
    })

    const updateMutation = useEntityMutation({
        mutationFn: (data: UpdateApprovalPolicyRequest) =>
            services.approvalPolicies.update(id!, data),
        invalidate: [['approval-policies'], ['approval-policy', id]],
        successMessage: i18n.approvalPolicies.policyUpdated,
        onSuccess: () => {
            navigate(routes.accessGovernance.approvalPolicies.detail(id!))
        },
    })

    const handleSubmit = async (data: CreateApprovalPolicyRequest | UpdateApprovalPolicyRequest) => {
        await updateMutation.mutateAsync(data as UpdateApprovalPolicyRequest)
    }

    const shouldRedirect = !canUpdate || (policy != null && policy.isDefault)

    useEffect(() => {
        if (shouldRedirect) {
            navigate(routes.accessGovernance.approvalPolicies.detail(id!), {replace: true})
        }
    }, [shouldRedirect, navigate, id])

    if (shouldRedirect) {
        return null
    }

    return (
        <PageLayout
            variant="detail"
            title={i18n.approvalPolicies.editPolicy}
            description={i18n.approvalPolicies.editDescription}
            icon={CONCEPT_ICON.approvalPolicy}
            backTo={true}
            loading={isLoading}
            error={queryError(isLoading, policy, fetchError)}
            data-testid="approval-policy-edit-page"
        >
            {policy && (
                <ApprovalPolicyForm
                    policy={policy}
                    onSubmit={handleSubmit}
                    onCancel={() => navigate(-1)}
                    isLoading={updateMutation.isPending}
                />
            )}
        </PageLayout>
    )
}
